import { createContext, ReactNode, useContext } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { insertUserSchema, User, InsertUser } from "@/lib/shared-schema";
import { getQueryFn, apiRequest, queryClient } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";

type AuthContextType = {
  user: User | null;
  isLoading: boolean;
  error: Error | null;
  loginMutation: UseMutationResult<User, Error, LoginData>;
  logoutMutation: UseMutationResult<void, Error, void>;
  registerMutation: UseMutationResult<User, Error, InsertUser>;
};

type LoginData = {
  username: string;
  password: string;
};

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  
  const {
    data: user,
    error,
    isLoading,
  } = useQuery<User | null, Error>({
    queryKey: ["/api/user"],
    queryFn: getQueryFn({ on401: "returnNull" }),
  });

  const loginMutation = useMutation({
    mutationFn: async (credentials: LoginData) => {
      const res = await apiRequest("POST", "/api/login", credentials);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed to login");
      }
      return await res.json();
    },
    onSuccess: async (userData: User) => {
      queryClient.setQueryData(["/api/user"], userData);
      toast({
        title: "Success",
        description: "Logged in successfully",
      });
      
      // Fetch subscription status to determine where to redirect
      try {
        const subscriptionResponse = await fetch("/api/stripe/subscription-status", {
          credentials: "include",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
          }
        });
        
        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          const tier = subscriptionData?.tier || 'solo';
          
          // Redirect based on subscription tier
          if (tier === 'pro' || tier === 'team') {
            setLocation("/dashboard");
          } else {
            setLocation("/engagements");
          }
        } else {
          // If subscription fetch fails, default to engagements
          setLocation("/engagements");
        }
      } catch (error) {
        console.error("Error fetching subscription status during login:", error);
        // Default to engagements if there's an error
        setLocation("/engagements");
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (userData: InsertUser) => {
      const res = await apiRequest("POST", "/api/register", userData);
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed to register");
      }
      return await res.json();
    },
    onSuccess: async (userData: User) => {
      queryClient.setQueryData(["/api/user"], userData);
      toast({
        title: "Success",
        description: "Account created successfully",
      });
      
      // Fetch subscription status to determine where to redirect
      try {
        const subscriptionResponse = await fetch("/api/stripe/subscription-status", {
          credentials: "include",
          headers: {
            "Accept": "application/json",
            "Content-Type": "application/json"
          }
        });
        
        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          const tier = subscriptionData?.tier || 'solo';
          
          // Redirect based on subscription tier
          if (tier === 'pro' || tier === 'team') {
            setLocation("/dashboard");
          } else {
            setLocation("/engagements");
          }
        } else {
          // If subscription fetch fails, default to engagements (new users are typically solo)
          setLocation("/engagements");
        }
      } catch (error) {
        console.error("Error fetching subscription status during registration:", error);
        // Default to engagements if there's an error (new users are typically solo)
        setLocation("/engagements");
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Registration failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/logout");
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed to logout");
      }
    },
    onSuccess: () => {
      queryClient.setQueryData(["/api/user"], null);
      queryClient.clear();
      toast({
        title: "Success",
        description: "Logged out successfully",
      });
      setLocation("/login");
    },
    onError: (error: Error) => {
      queryClient.setQueryData(["/api/user"], null);
      queryClient.clear();
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
      setLocation("/login");
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        isLoading,
        error,
        loginMutation,
        logoutMutation,
        registerMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}